/**
 * CassKai — Accès au stockage local qui survit à son absence
 * Copyright © 2025-2026 NOUTCHE CONSEIL (SIREN 909 672 685)
 *
 * ── Pourquoi ce module existe ─────────────────────────────────────────────
 *
 * `window.localStorage` n'est pas toujours là. Dans un WebView Android dont le
 * stockage DOM est désactivé, il vaut **null** — il ne lève pas, il est absent.
 * En navigation privée ou sous politique d'entreprise, il existe mais y toucher
 * lève une `SecurityError`. Les deux formes se rencontrent en production.
 *
 * Le 22/08/2026, un utilisateur d'Abidjan sur TECNO KL5 (Android 14, Chrome
 * Mobile WebView) a fait tomber le démarrage de l'application :
 * `initializeTheme()` s'exécute au niveau module dans `main.tsx`, donc AVANT le
 * montage de React, et lisait `localStorage` sans garde. L'exception n'était
 * rattrapée par personne — écran blanc, pas dégradation. CASSKAI-FRONTEND-8Z
 * (non rattrapée) et CASSKAI-FRONTEND-90 (rattrapée, mais message générique)
 * portent le même `trace_id` : une seule cause, deux symptômes.
 *
 * ── Ce que ce module fait, et surtout ce qu'il ne fait PAS ────────────────
 *
 * Il ne remplace pas le stockage et ne conserve rien en mémoire à sa place :
 * ce serait promettre une persistance qui n'existe pas. Il rend son absence
 * LISIBLE.
 *
 *   - une lecture impossible renvoie `null` — exactement ce que renvoie déjà
 *     `localStorage.getItem` pour une clé absente. Les appelants savent tous
 *     traiter ce cas, donc aucun d'eux n'a besoin de changer ;
 *   - une écriture impossible renvoie `false` au lieu de prétendre avoir
 *     réussi. L'appelant décide s'il refuse, prévient, ou continue.
 *
 * ⚠️ Ne pas transformer « absent » en autre chose (chaîne vide, valeur par
 * défaut inventée) : ce serait exactement le faux plausible que ce dépôt
 * combat ailleurs.
 */

/** Renvoie l'objet de stockage, ou `null` s'il est indisponible. */
function stockage(): Storage | null {
  try {
    // Le `typeof` protège d'un environnement sans `globalThis.localStorage`
    // (rendu serveur, worker) ; le test de nullité protège du WebView Android.
    if (typeof globalThis === 'undefined') return null;
    const brut = (globalThis as { localStorage?: Storage | null }).localStorage;
    return brut ?? null;
  } catch {
    // L'ACCÈS lui-même peut lever : navigation privée, cookies tiers bloqués.
    return null;
  }
}

/** Le stockage local est-il utilisable ici et maintenant ? */
export function stockageLocalDisponible(): boolean {
  return stockage() !== null;
}

/**
 * Lit une clé. Renvoie `null` si la clé est absente **ou** si le stockage est
 * indisponible — les deux cas se traitent de la même façon côté appelant.
 */
export function lireStockageLocal(cle: string): string | null {
  const s = stockage();
  if (!s) return null;
  try {
    return s.getItem(cle);
  } catch {
    return null;
  }
}

/**
 * Écrit une clé. Renvoie `false` si l'écriture n'a pas pu aboutir — stockage
 * absent, quota dépassé, accès refusé. Ne lève jamais.
 */
export function ecrireStockageLocal(cle: string, valeur: string): boolean {
  const s = stockage();
  if (!s) return false;
  try {
    s.setItem(cle, valeur);
    return true;
  } catch {
    // `QuotaExceededError` notamment : le stockage existe mais est plein.
    return false;
  }
}

/**
 * Adaptateur pour les fonctions qui reçoivent un `Storage` par injection.
 *
 * `versionGuard.retenirMinSafe(stockage, …)` a raison de prendre son stockage
 * en paramètre — c'est ce qui la rend testable. Mais lui passer `localStorage`
 * brut la fait lever quand celui-ci est null. On lui passe donc cet adaptateur,
 * qui présente la même forme et ne lève jamais.
 */
export const stockageLocalSur: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = {
  getItem: (cle: string) => lireStockageLocal(cle),
  setItem: (cle: string, valeur: string) => {
    ecrireStockageLocal(cle, valeur);
  },
  removeItem: (cle: string) => {
    supprimerStockageLocal(cle);
  },
};

/** Supprime une clé. Renvoie `false` si l'opération n'a pas pu aboutir. */
export function supprimerStockageLocal(cle: string): boolean {
  const s = stockage();
  if (!s) return false;
  try {
    s.removeItem(cle);
    return true;
  } catch {
    return false;
  }
}
